import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import LandingPage from './pages/LandingPage';
import Dashboard from './components/Dashboard';
import EnterpriseDMS from './pages/EnterpriseDMS';
import DocumentationPage from './pages/DocumentationPage';
import SessionManager from './components/SessionManager';
import VerifyEmail from './pages/VerifyEmail';
import ResetPassword from './pages/ResetPassword';
import AuthPage from './pages/AuthPage';

import { API_BASE_URL } from './config';

// Helper to validate JWT expiration
const isTokenValid = (token) => {
  if (!token) return false;
  try {
    const base64Url = token.split('.')[1];
    let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) base64 += '=';
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    const payload = JSON.parse(jsonPayload);
    // exp is in seconds, Date.now() is in ms
    if (payload.exp && (payload.exp * 1000) < Date.now()) {
      return false; // Token expired
    }
    return true;
  } catch (e) {
    return false; // Invalid token format
  }
};

function App() {
  // Initialize state from Hash immediately to prevent "Hash Fighting"
  const [currentView, setCurrentView] = useState(() => {
    const hash = window.location.hash;

    // ── Tab-close security ──────────────────────────────────────────────────
    // sessionStorage is wiped when the user closes the tab.
    // If our sentinel flag is absent this is a brand-new or re-opened tab.
    // In that case, force the user to re-login, even if a token persists in localStorage.
    const tabAlive   = sessionStorage.getItem('datalift_tab_alive');
    const tabLogged  = sessionStorage.getItem('datalift_tab_logged_in');
    if (!tabAlive && !tabLogged) {
      console.log('[App] Fresh tab detected – clearing auth token to force re-login.');
      localStorage.removeItem('dms_auth_token');
      sessionStorage.removeItem('dms_auth_token');
    }

    const rawToken = localStorage.getItem('dms_auth_token') || sessionStorage.getItem('dms_auth_token');

    // Validate JWT
    let hasToken = false;
    if (rawToken) {
      if (isTokenValid(rawToken)) {
        hasToken = true;
      } else {
        // Token is expired or invalid, clear it
        console.log('[App] Initial check: Token expired. Clearing session.');
        localStorage.removeItem('dms_auth_token');
        sessionStorage.removeItem('dms_auth_token');
      }
    }

    if (hash === '#/docs') return 'docs-only';
    if (hash.startsWith('#/verify-email')) return 'verify-email';
    if (hash.startsWith('#/reset-password')) return 'reset-password';
    if (hash === '#/login' || hash === '#/register') return 'auth';
    // Auth guard: only allow DMS view if there's a valid token AND tab is alive
    if (hash.startsWith('#/dms') && hasToken) return 'dms';
    if (hash === '#/' || !hash || !hasToken) return 'landing';
    return 'landing';
  });

  const [authMode, setAuthMode] = useState(() => {
    const hash = window.location.hash;
    return hash === '#/register' ? 'register' : 'login';
  });

  const [activeSection, setActiveSection] = useState('connections');

  const [initialDmsView, setInitialDmsView] = useState(() => {
    const hash = window.location.hash;

    // If logging in via SSO, force the dashboard view on initial render
    if (hash.includes('token=')) {
      return 'dashboard';
    }

    if (hash.startsWith('#/dms/')) {
      const dmsView = hash.replace('#/dms/', '');
      // Strip any query parameters for initial state calculation
      const cleanView = dmsView.split('?')[0];
      return cleanView;
    }
    return null;
  });

  // Theme State
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'dark';
  });

  useEffect(() => {
    // Force the landing page to always render in dark mode
    if (currentView === 'landing' || currentView === 'auth') {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.setAttribute('data-theme', theme);
    }
    localStorage.setItem('theme', theme);
  }, [theme, currentView]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'dark' ? 'light' : 'dark');
  };

  const [connections, setConnections] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  }, []);

  const handleStartMigration = () => {
    setCurrentView('dashboard');
    setActiveSection('connections');
  };

  const handleStartDMS = () => {
    // Mark this tab as authenticated so SessionManager idle-tracker can start
    sessionStorage.setItem('datalift_tab_alive',     '1');
    sessionStorage.setItem('datalift_tab_logged_in', '1');
    setInitialDmsView('dashboard');
    setCurrentView('dms');
  };

  const handleStartSettings = () => {
    setInitialDmsView('settings');
    setCurrentView('dms');
  };

  const refreshConnections = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/connections`);
      const data = await response.json();
      if (data.success) {
        setConnections(data.connections || []);
      }
    } catch (error) {
      console.error('Error fetching connections:', error);
      showNotification('Failed to fetch connections', 'error');
    }
  }, [showNotification]);

  const refreshJobs = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/migrations`);
      const data = await response.json();
      if (data.success) {
        setJobs(data.jobs || []);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      showNotification('Failed to fetch jobs', 'error');
    }
  }, [showNotification]);

  useEffect(() => {
    if (currentView === 'dashboard') {
      refreshConnections();
      refreshJobs();
    }
  }, [currentView, refreshConnections, refreshJobs]);

  // Check for deep links (e.g., #/docs) and SSO tokens
  useEffect(() => {
    const handleHashChange = () => {
      const rawHash = window.location.hash;
      const rawSearch = window.location.search;
      const [hashPath, hashQueryString] = rawHash.split('?');

      // Look for token in both search (before hash) and hashQueryString (after hash)
      let token = null;
      if (rawSearch && rawSearch.includes('token=')) {
        token = new URLSearchParams(rawSearch).get('token');
      } else if (hashQueryString && hashQueryString.includes('token=')) {
        token = new URLSearchParams(hashQueryString).get('token');
      }

      if (token) {
        localStorage.setItem('dms_auth_token', token);
        // Notify same-tab listeners (e.g. EnterpriseDMS header) that auth has been updated
        window.dispatchEvent(new Event('dms-auth-updated'));
        // Clean up the URL by removing the token
        const cleanSearch = window.location.search.replace(/(\?|&)token=[^&]*/, '');
        window.history.replaceState(null, '', window.location.pathname + cleanSearch + '#/dms/dashboard');
        setCurrentView('dms');
        setInitialDmsView('dashboard');
        return;
      }

      if (hashPath === '#/login') {
        setCurrentView('auth');
        setAuthMode('login');
      } else if (hashPath === '#/register') {
        setCurrentView('auth');
        setAuthMode('register');
      } else if (hashPath === '#/docs') {
        setCurrentView('docs-only');
      } else if (hashPath.startsWith('#/verify-email')) {
        setCurrentView('verify-email');
      } else if (hashPath.startsWith('#/reset-password')) {
        setCurrentView('reset-password');
      } else if (hashPath.startsWith('#/dms')) {
        // Auth guard: redirect to login if no token or expired
        const rawToken = localStorage.getItem('dms_auth_token') || sessionStorage.getItem('dms_auth_token');
        if (!rawToken || !isTokenValid(rawToken)) {
          console.warn('[App.js] Token invalid or missing. Redirecting to login.');
          if (rawToken) {
            localStorage.removeItem('dms_auth_token');
            sessionStorage.removeItem('dms_auth_token');
          }
          window.history.replaceState(null, '', window.location.pathname + '#/login');
          setCurrentView('auth');
          setAuthMode('login');
          return;
        }
        const dmsView = hashPath.replace('#/dms/', '');
        setCurrentView('dms');
        setInitialDmsView(dmsView || 'dashboard');
      } else if (hashPath === '#/' || hashPath === '') {
        setCurrentView('landing');
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    handleHashChange(); // Initial check
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  return (
    <div className="app">
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      {(currentView === 'dms' || currentView === 'dashboard') && <SessionManager />}

      {currentView === 'verify-email' ? (
        <VerifyEmail />
      ) : currentView === 'reset-password' ? (
        <ResetPassword />
      ) : currentView === 'docs-only' ? (
        <DocumentationPage isFullPage={true} theme={theme} toggleTheme={toggleTheme} />
      ) : currentView === 'auth' ? (
        <AuthPage
          theme={theme}
          initialMode={authMode}
          onBack={() => {
            window.location.hash = '#/';
          }}
          onLoginSuccess={handleStartDMS}
        />
      ) : currentView === 'landing' ? (
        <LandingPage
          onStartMigration={handleStartMigration}
          onStartDMS={handleStartDMS}
          onOpenSettings={handleStartSettings}
          theme={theme}
          toggleTheme={toggleTheme}
        />
      ) : currentView === 'dms' ? (
        <EnterpriseDMS
          theme={theme}
          toggleTheme={toggleTheme}
          initialView={initialDmsView}
          onBack={() => {
            setInitialDmsView(null);
            setCurrentView('landing');
          }}
        />
      ) : (
        <Dashboard
          apiUrl={API_BASE_URL}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          connections={connections}
          jobs={jobs}
          loading={loading}
          setLoading={setLoading}
          showNotification={showNotification}
          onRefreshConnections={refreshConnections}
          onRefreshJobs={refreshJobs}
          theme={theme}
          toggleTheme={toggleTheme}
          onBackToHome={() => setCurrentView('landing')}
          onOpenSettings={handleStartSettings}
        />
      )}
    </div>
  );
}

export default App;
